import { useState, useRef, useEffect, type MouseEvent } from 'react';

interface SearchableSelectProps {
  options: string[];
  /** 单选：string；多选：string[] */
  value: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  allLabel?: string;
  icon?: string;
  searchPlaceholder?: string;
  /** 开启后可多选，点击选项不自动关闭 */
  multiple?: boolean;
  /** 校验失败时危险色描边 */
  invalid?: boolean;
  /** 是否显示清空按钮与「全部」选项，默认 true */
  clearable?: boolean;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = '全部...',
  allLabel = '全部',
  icon,
  searchPlaceholder = '搜索...',
  multiple = false,
  invalid = false,
  clearable = true,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedValues = multiple
    ? Array.isArray(value)
      ? value
      : value
        ? [value]
        : []
    : [];
  const singleValue = !multiple ? (typeof value === 'string' ? value : value[0] || '') : '';

  const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as HTMLElement)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const toggleMulti = (opt: string) => {
    if (selectedValues.includes(opt)) {
      onChange(selectedValues.filter((v) => v !== opt));
    } else {
      onChange([...selectedValues, opt]);
    }
    // 保留当前搜索词与过滤结果，便于连续多选
    inputRef.current?.focus();
  };

  const clearAll = () => {
    onChange(multiple ? [] : '');
    setSearch('');
    if (!multiple) {
      setOpen(false);
    } else {
      inputRef.current?.focus();
    }
  };

  const removeOne = (opt: string, e: MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter((v) => v !== opt));
  };

  const openDropdown = () => {
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const inputPlaceholder = multiple
    ? selectedValues.length === 0
      ? placeholder
      : searchPlaceholder
    : open
      ? searchPlaceholder
      : singleValue || placeholder;

  const hasClear = clearable && (multiple ? selectedValues.length > 0 : !!singleValue);

  return (
    <div ref={containerRef} className="relative">
      <div
        role="combobox"
        aria-expanded={open}
        tabIndex={-1}
        onClick={openDropdown}
        className={`flex items-center gap-1.5 px-2.5 h-[38px] text-sm border rounded-lg bg-white transition-colors cursor-text w-full min-w-0 overflow-hidden ${
          invalid
            ? 'border-red-500 ring-1 ring-red-200'
            : open
              ? 'border-primary-400'
              : 'border-background-200 hover:border-primary-400'
        }`}
      >
        {icon && <i className={`${icon} text-foreground-400 text-xs shrink-0`} />}

        <div className="flex-1 min-w-0 flex items-center gap-1 flex-nowrap overflow-hidden">
          {multiple && selectedValues.length > 1 ? (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-700 border border-primary-100 whitespace-nowrap shrink-0">
              已选 {selectedValues.length} 个科室
            </span>
          ) : multiple && selectedValues.length === 1 ? (
            <span className="inline-flex items-center gap-0.5 max-w-[55%] px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-700 border border-primary-100 shrink min-w-0">
              <span className="truncate">{selectedValues[0]}</span>
              <button
                type="button"
                onClick={(e) => removeOne(selectedValues[0], e)}
                className="shrink-0 hover:text-primary-900 leading-none"
                aria-label={`移除 ${selectedValues[0]}`}
              >
                <i className="ri-close-line text-[11px]" />
              </button>
            </span>
          ) : null}

          {!multiple && !open && singleValue ? (
            <span className="text-foreground-800 truncate px-0.5 min-w-0">{singleValue}</span>
          ) : null}

          {(open || multiple || !singleValue) && (
            <input
              ref={inputRef}
              type="text"
              value={search}
              placeholder={inputPlaceholder}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && multiple && !search && selectedValues.length > 0) {
                  onChange(selectedValues.slice(0, -1));
                }
                if (e.key === 'Escape') {
                  setOpen(false);
                  setSearch('');
                  inputRef.current?.blur();
                }
              }}
              className="min-w-[4rem] flex-1 outline-none bg-transparent text-sm py-0.5 placeholder:text-foreground-400 truncate"
            />
          )}
        </div>

        <div className="w-4 h-4 shrink-0 flex items-center justify-center">
          {hasClear && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
              className="text-foreground-300 hover:text-foreground-500 leading-none"
              aria-label="清空"
            >
              <i className="ri-close-circle-fill text-sm" />
            </button>
          )}
        </div>

        <i
          className={`ri-arrow-down-s-line text-xs text-foreground-400 shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </div>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-full min-w-[200px] bg-white border border-background-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="max-h-52 overflow-y-auto">
            {clearable && (
              <button
                type="button"
                onClick={clearAll}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-background-50 transition-colors whitespace-nowrap ${
                  (multiple ? selectedValues.length === 0 : !singleValue)
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-foreground-700'
                }`}
              >
                {allLabel}
              </button>
            )}
            {filtered.map((opt) => {
              const checked = multiple ? selectedValues.includes(opt) : singleValue === opt;
              return (
                <button
                  type="button"
                  key={opt}
                  onClick={() => {
                    if (multiple) {
                      toggleMulti(opt);
                    } else {
                      onChange(opt);
                      setOpen(false);
                      setSearch('');
                    }
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-background-50 transition-colors whitespace-nowrap flex items-center gap-2 ${
                    checked ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-700'
                  }`}
                >
                  {multiple && (
                    <i
                      className={`${
                        checked ? 'ri-checkbox-fill text-primary-600' : 'ri-checkbox-blank-line text-foreground-300'
                      } text-sm shrink-0`}
                    />
                  )}
                  <span className="truncate">{opt}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-foreground-400 text-center">无匹配结果</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
